import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from "react-router-dom";
import {
  Add
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  ModelBase,
} from "@models";
import { 
  Spell, 
  AbilityEffect,
  SpellList,
} from "@dndModels";
import { 
  DURATIONS,
  COMPONENTS,
  SCHOOLS,
  STANDARD_CASTING_TIMES,
  DAMAGE_TYPES,
} from "@dndModels/Constants";
import { CastingTime, DamageType, DurationType, isDamageType, isSchoolOfMagic, isSpellLevel, SchoolOfMagic, SpellLevel } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import LoadingBox from "@components/input/LoadingBox";

import AbilityEffectInput from "@dndComponents/feature/AbilityEffectInput";
import ModelBaseInput from "@dndComponents/ModelBaseInput";
import ButtonBox from "@components/input/buttons/ButtonBox";
import SelectSourceBookBox from '@dndComponents/select/SelectSourceBookBox';
// import FilterBox from '../../components/FilterBox';
// import ItemLimitInput from '../../components/limitations/ItemLimitInput';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function SpellEdit() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [handling_spell_lists, setHandlingSpellLists] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Spell>(api.applyWorkingOn(new Spell()));
  const [spell_lists, setSpellLists] = useState<SpellList[]>([]);
  const [change_time, setChangeTime] = useState(0);
  const [spell_list_ids, setSpellListIds] = useState<string[]>([]);
  const [old_spell_list_ids, setOldSpellListIds] = useState<string[]>([]);
  const [show_effect, setShowEffect] = useState("");
  const [sl_sb_type, setSLSBType] = useState(api.working_on_sb?.source_book_type??"Source Book");
  const [sl_sb_id, setSLSBId] = useState(api.working_on_sb?._id??"");
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
  useEffect(() => {
    if(!fetched) {
      load();
    }
  });
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
    
  useEffect(() => {
    if (fetched && !loading) {
      handleSpellLists();
    }
    // eslint-disable-next-line
  }, [handling_spell_lists]);

  const load = () => {
    setFetched(true);
    // debugger;
    setLoading(true);
    api.getSetOfObjects({ data_types: ["spell_list"]}).then(res => {
      const new_spell_lists = res.spell_list as SpellList[];
      setSpellLists(new_spell_lists);

      if (id && id !== "") {
        const new_spell_list_ids = new_spell_lists.filter(o => api.fitSpellList({ spell: obj, spell_list: o, sb_id: sl_sb_id })).map(spell_list => {
          return spell_list._id;
        });
        setSpellListIds(new_spell_list_ids);
        load_object(id);
      } else {
        // It's here from clicking Create on index.  
        // If the user is not admin, then we need to give the floating feature their id as owner or it will be disabled.
        const new_obj = obj;
        if (api.user && !api.user.admin && new_obj.name === "" && new_obj.owner_id === "") {
          new_obj.owner_id = api.user._id;
        }
        setObj(new_obj);
        setLoading(false);
      }
    });
  }

  // Loads the editing Creature into state
  const load_object = (id: string) => {
    api.getFullObject("spell", id).then(res => {
      if (res instanceof Spell) {
        setObj(res);
        // setSpellListIds(res.spell_list_ids);
        const new_spell_list_ids = spell_lists.filter(o => 
          res.spell_list_ids.includes(o._id) && 
          api.fitSpellList({ spell: obj, spell_list: o, sb_id: sl_sb_id })
        ).map(spell_list => {
          return spell_list._id;
        });
        setSpellListIds(new_spell_list_ids);
        setLoading(false);
      }
    });
  }

  const submit = () => {
    if (api.user && 
      (api.user.admin || 
        obj._id === "" || 
        obj.owner_id === api.user._id)) {
      setProcessing(true);
      if (!api.user.admin || obj.source_books_old.filter(o => o.id === "Personal").length > 0) {
        obj.owner_id = api.user._id;
      } else if (api.user.admin && obj.source_books_old.filter(o => o.type === "Homebrew").length === 0) {
        obj.owner_id = "";
      }
      obj.spell_list_ids = spell_list_ids;
      api.upsertObject("spell", obj).then(res => {
        console.log(res);
        if (res && res.body.id) {
          obj._id = res.body.id;
          setHandlingSpellLists(!handling_spell_lists);
        } else {
          setHandlingSpellLists(!handling_spell_lists);
        }
      });
    }
  }

  const handleSpellLists = () => {
    let new_old_spell_list_ids = [...old_spell_list_ids];
    const new_spell_list_ids = spell_list_ids.filter(o => !new_old_spell_list_ids.includes(o));
    if (new_spell_list_ids.length > 0) {
      const spell_list_id = new_spell_list_ids[0];
      const spell_list_finder = spell_lists.filter(o => o._id === spell_list_id);
      if (spell_list_finder.length === 1) {
        const spell_list = spell_list_finder[0];
        spell_list.pushSpellId(obj._id, sl_sb_id);
        api.upsertObject("spell_list", spell_list).then(res => {
          new_old_spell_list_ids.push(spell_list._id);
          setOldSpellListIds(new_old_spell_list_ids);
          setHandlingSpellLists(!handling_spell_lists);
        });
      }
    } else {
      const removing_spell_list_ids = new_old_spell_list_ids.filter(o => !spell_list_ids.includes(o));
      if (removing_spell_list_ids.length > 0) {
        const spell_list_id = removing_spell_list_ids[0];
        const spell_list_finder = spell_lists.filter(o => o._id === spell_list_id);
        if (spell_list_finder.length === 1) {
          const spell_list = spell_list_finder[0];
          spell_list.removeSpellId(obj._id, sl_sb_id);
          api.upsertObject("spell_list", spell_list).then(res => {
            new_old_spell_list_ids = new_old_spell_list_ids.filter(o => o !== spell_list._id);
            setOldSpellListIds(old_spell_list_ids);
            setHandlingSpellLists(!handling_spell_lists);
          });
        }
      } else {
        setProcessing(false);
        setNavigateTo("/dandd/spell");
      }
    }
  }

  const parseDescription = () => {
    // Look for things we can pull from the description to help figure out the details of the spell.
    const lines = obj.description.split("\n");
    console.log(lines);
    const new_obj = obj;
    let range = 0;
    for (const line of lines) {
      if (line.startsWith("Level")) {
        // Level 1 Divination (Bard, Cleric, Druid, Paladin, Ranger, Wizard)
        const pieces = line.split("(");
        const spell_info = pieces[0].split(" ");
        const level = +spell_info[1];
        if (isSpellLevel(level)) {
          new_obj.level = level;
        }
        new_obj.chronomancy = line.includes("Chronomancy");
        new_obj.dunamancy = line.includes("Dunamancy");
        const school = spell_info[2];
        new_obj.school = isSchoolOfMagic(school) ? school : "";
        new_obj.spell_list_ids = [];
        const spell_lists = pieces[1].replace(")","").split(", ");
        for (const sl of spell_lists) {
          const spell_list = api.smart_hash.spell_list.find(o => o.name === sl && o.isDominant());
          if (spell_list) {
            new_obj.spell_list_ids.push(spell_list._id);
          }
        }
      } else if (line.includes("Cantrip Upgrade.") || line.includes("Cantrip Upgrade:") || line.includes("Using a Higher-Level Spell Slot.") || line.includes("Using a Higher-Level Spell Slot:")) {
        // Look for potence improvements
      } else if (line.includes("Cantrip") && line.includes("(")) {
        // Evocation Cantrip (Bard, Sorcerer, Wizard)
        const pieces = line.split("(");
        const spell_info = pieces[0].split(" ");
        new_obj.level = 0;
        new_obj.chronomancy = line.includes("Chronomancy");
        new_obj.dunamancy = line.includes("Dunamancy");
        const school = spell_info[0];
        new_obj.school = isSchoolOfMagic(school) ? school : "";
        new_obj.spell_list_ids = [];
        const spell_lists = pieces[1].replace(")","").split(", ");
        for (const sl of spell_lists) {
          const spell_list = api.smart_hash.spell_list.find(o => o.name === sl && o.isDominant());
          if (spell_list) {
            new_obj.spell_list_ids.push(spell_list._id);
          }
        }
      } else if (line.includes("Casting Time:")) {
        // <b>Casting Time:</b> Action or Ritual, Casting Time: 1 hour or Ritual
        if (line.includes("ction")) {
          if (line.includes("Reaction")) {
            new_obj.casting_time = "RA";
          } else if (line.includes("Bonus")) {
            new_obj.casting_time = "BA";
          } else {
            new_obj.casting_time = "A";
          }
        } else {
          new_obj.casting_time = "Special";
          if (line.includes("1 Minute")) {
            new_obj.special_casting_time = "1 Minute";
          } else if (line.includes("10 Minutes")) {
            new_obj.special_casting_time = "10 Minutes";
          } else if (line.includes("1 Hour")) {
            new_obj.special_casting_time = "1 Hour";
          } else if (line.includes("8 Hours")) {
            new_obj.special_casting_time = "8 Hours";
          } else if (line.includes("12 Hours")) {
            new_obj.special_casting_time = "12 Hours";
          } else if (line.includes("24 Hours")) {
            new_obj.special_casting_time = "24 Hours";
          }
        }
        new_obj.ritual = line.includes("Ritual");
      } else if (line.includes("Range:")) {
        // <b>Range:</b> Touch, Range: Self, Range: 60 feet
        if (line.includes("feet") || 
          line.includes("ft")) {
          const pieces = line.split(" ");
          for (const piece of pieces) {
            const num = +piece;
            if (!isNaN(num)) {
              range = num;
              break;
            }
          }
        }
      } else if (line.includes("Components:")) {
        // <b>Components:</b> V, S, M (a magnifying glass and pipe)
        const pieces = line.split("(");
        const comps = pieces[0].split(" ").map(o => o.replace(",", ""));
        new_obj.components = [];
        if (comps.includes("V")) {
          new_obj.components.push("V");
        }
        if (comps.includes("S")) {
          new_obj.components.push("S");
        }
        if (comps.includes("M")) {
          new_obj.components.push("M");
          if (pieces.length > 1) {
            new_obj.material_component = pieces[1].replace(")", "");
          }
        }
      } else if (line.includes("Duration:")) {
        // Duration: Concentration, up to 10 minutes, Duration: Instantaneous, Duration: 1 hour, etc
        if (line.includes("Instantaneous")) {
          new_obj.duration = "Instantaneous";
        } else {
          if (line.includes("Concentration")) {
            new_obj.concentration = true;
          } 
          let found = false;
          for (const dur of DURATIONS) {
            if (line.includes(dur)) {
              new_obj.duration = dur;
              found = true;
              break;
            }
          }
          if (!found) {
            new_obj.duration = "Special";
            new_obj.special_duration = line.replace("<b>","").replace("</b>","").replace("Duration:","").trim();
          }
        }
      } else {
        // The actual spell description. Try to figure out the actual effects.
        const lc = line.toLocaleLowerCase();
        if (lc.includes("contamination")) {
          new_obj.contaminated = true;
        }
        if (lc.includes("sight") || lc.includes("see")) {
          new_obj.sight = true;
        }
        if (lc.includes("charm")) {
          new_obj.charm = true;
        }
        if (lc.includes("disease")) {
          new_obj.disease = true;
        }
        const dt_found_index: { index: number, dt: DamageType }[] = [];
        for (const dt of DAMAGE_TYPES) {
          if (isDamageType(dt)) {
            const index = lc.indexOf(dt.toLocaleLowerCase());
            if (index > -1) {
              dt_found_index.push({ index, dt });
            }
          }
        }
        new_obj.effects = [];
        const effect = new AbilityEffect();
        effect.target_int.range.base = range;
        if (dt_found_index.length > 0) {
          const sorted = dt_found_index.sort((a, b) => (a.index > b.index ? 1 : -1));
          effect.type = "Damage";
          for (const dt of sorted) {
            effect.damage_types.push(dt.dt);
          }
        } else {
          effect.type = "Utility";
        }
        new_obj.effects.push(effect);
      }
    }
    setObj(new_obj);
    const new_spell_list_ids = spell_lists.filter(o => 
      new_obj.spell_list_ids.includes(o._id) && 
      api.fitSpellList({ spell: obj, spell_list: o, sb_id: sl_sb_id })
    ).map(spell_list => {
      return spell_list._id;
    });
    setSpellListIds(new_spell_list_ids);
    setChangeTime(common_util.get_time_number());
    setReloading(true);
  }
  
  const height = window.innerHeight - 64;
  if (!fetched || loading) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <ModelBaseInput 
        obj={obj}
        disabled={disabled}
        change_time={change_time}
        processing={processing}
        onChange={(changed: ModelBase) => {
          if (changed instanceof Spell) {
            const new_obj = obj;
            new_obj.copy(changed);
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }
        }}
        onSubmit={submit}>
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <ButtonBox
              name="Parse Description (Effects will definitely need work)"
              disabled={disabled}
              onClick={parseDescription}
            />
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              size={{
                xs: 4
              }}>
              <SelectStringBox 
                options={["0","1","2","3","4","5","6","7","8","9"]}
                value={`${obj.level}`} 
                name="Spell Level"
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.level = +changed as SpellLevel;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 4
              }}>
              <Grid
                size={{
                  xs: 9
                }}>
                <SelectStringBox 
                  name="Casting Time"
                  options={STANDARD_CASTING_TIMES}
                  extra_options={["Special"]}
                  value={obj.casting_time}
                  disabled={disabled}
                  onChange={(changed: CastingTime) => {
                    const new_obj = obj;
                    new_obj.casting_time = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                /> 
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <CheckBox 
                  name="R" 
                  value={obj.ritual} 
                  disabled={disabled}
                  onChange={(changed: boolean) => {
                    const new_obj = obj;
                    new_obj.ritual = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }} 
                />
              </Grid>
              { obj.casting_time === "Special" && 
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <StringBox 
                    name="Special Casting Time"
                    value={obj.special_casting_time}
                    disabled={disabled}
                    onBlur={(changed: string) => {
                      const new_obj = obj;
                      new_obj.special_casting_time = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  /> 
                </Grid>
              }
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <SelectStringBox 
                name="Components"
                options={COMPONENTS}
                values={obj.components}
                multiple
                disabled={disabled}
                onChange={(changed: ("V" | "S" | "M")[]) => {
                  const new_obj = obj;
                  let m_changed = false;
                  if (new_obj.components.includes("M") !== changed.includes("M")) {
                    new_obj.material_component = "";
                    m_changed = true;
                  }
                  new_obj.components = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  if (m_changed) {
                    setReloading(true);
                  }
                }}
              /> 
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 6
              }}>
              <Grid
                size={{
                  xs: obj.duration === "Instantaneous" ? 12 : 9
                }}>
                <SelectStringBox 
                  name="Duration"
                  options={DURATIONS}
                  disabled={disabled}
                  value={ obj.duration }
                  onChange={(changed: DurationType) => {
                    const new_obj = obj;
                    new_obj.duration = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                /> 
              </Grid>
              { obj.duration !== "Instantaneous" && 
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <CheckBox 
                    name="C" 
                    value={obj.concentration} 
                    disabled={disabled}
                    onChange={(e: boolean) => {
                      const new_obj = obj;
                      new_obj.concentration = e;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }} 
                  />
                </Grid>
              } 
              { obj.duration === "Special" && 
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <StringBox 
                    name="Duration"
                    disabled={disabled}
                    value={obj.special_duration}
                    onBlur={(changed: string) => {
                      const new_obj = obj;
                      new_obj.special_duration = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  /> 
                </Grid>
              } 
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <SelectStringBox 
                options={SCHOOLS}
                auto_sort
                value={obj.school} 
                name="School"
                disabled={disabled}
                onChange={(changed: SchoolOfMagic) => {
                  const new_obj = obj;
                  new_obj.school = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="Twinnable" 
                value={obj.twinnable_1dd_ua7} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.twinnable_1dd_ua7 = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="Disease" 
                value={obj.disease} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.disease = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="Charm" 
                value={obj.charm} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.charm = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="Contaminated" 
                value={obj.contaminated} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.contaminated = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="Sight" 
                value={obj.sight} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.sight = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="Chronomancy" 
                value={obj.chronomancy} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.chronomancy = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="Dunamancy" 
                value={obj.dunamancy} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.dunamancy = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
          </Grid>
          { !reloading && obj.components.includes("M") &&
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <StringBox 
                  name="Material Component"
                  disabled={disabled}
                  value={obj.material_component}
                  multiline
                  onBlur={(changed: string) => {
                    const new_obj = obj;
                    new_obj.material_component = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                /> 
              </Grid>
            </>
          }
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 12
            }}>
            <Grid>
              <span className="header">
                Effects
              </span>
              { !disabled &&
                <Tooltip title={`Add Effect`}>
                  <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                    onClick={ () => {
                      const new_obj = obj;
                      const effect = new AbilityEffect();
                      if (obj.level === 0) {
                        effect.potence_type = "Character";
                      } else {
                        effect.potence_type = "Slot";
                      }
                      new_obj.effects.push(effect);
                      setObj(new_obj);
                      setShowEffect(effect.true_id);
                      setChangeTime(common_util.get_time_number());
                      setReloading(true);
                    }}>
                    <Add/>
                  </Fab>
                </Tooltip>
              }
            </Grid>
            { !reloading && obj.effects.map((effect, key) => {
              let name = effect.type === "Damage" ? effect.damage_types.join(", ") : effect.type;
              if (!["None","Self Condition"].includes(effect.type) && effect.attack_type !== "None") {
                name += ` ${effect.attack_type}`
              }

              return (
                <Grid key={key} container spacing={0} direction="column">
                  <Grid>
                    <ToggleButtonBox
                      name={ name }
                      // disabled={disabled}
                      value={ show_effect === effect.true_id }
                      onToggle={ () => { 
                        setShowEffect(show_effect === effect.true_id ? "" : effect.true_id);
                      }}
                    />
                  </Grid>
                  { show_effect === effect.true_id && 
                    <Grid>
                      <AbilityEffectInput 
                        obj={effect}
                        disabled={disabled}
                        slot_level={obj.level}
                        for_obj={obj}
                        onChange={() => {
                          const new_obj = obj;
                          setObj(new_obj);
                          setChangeTime(common_util.get_time_number());
                        }}
                        onDelete={() => {
                          const new_obj = obj;
                          new_obj.effects = new_obj.effects.filter(o => o.true_id !== effect.true_id);
                          setObj(new_obj);
                          setReloading(true);
                          setChangeTime(common_util.get_time_number());
                        }}
                      />
                    </Grid>        
                  }
                </Grid>
              );
            })}
          </Grid>
          { !reloading &&
            <>
              <Grid size={6}>
                <SelectSourceBookBox 
                  name="Spell List Source Book"
                  type={sl_sb_type}
                  id={sl_sb_id}
                  disabled={disabled}
                  onChange={(type, id) => {
                    setSLSBType(type);
                    setSLSBId(id);
                    const new_spell_list_ids = spell_lists.filter(o => api.fitSpellList({ spell: obj, spell_list: o, sb_id: id })).map(spell_list => {
                      return spell_list._id;
                    });
                    setSpellListIds(new_spell_list_ids);
                  }}
                />
              </Grid>
              <Grid size={6}>
                <SelectObjectBox 
                  name="Spell Lists"
                  data_type="spell_list"
                  values={spell_list_ids}
                  multiple
                  disabled={disabled}
                  onChange={setSpellListIds}
                />
              </Grid>
            </>
          }
        </Grid>
      </ModelBaseInput>
    ); 
  }
}